"use client"

import { useEffect, useState } from "react"
import { ChevronLeft, ChevronRight, X, Maximize2, Plus } from "lucide-react"
import Image from "next/image"
import { Swiper, SwiperSlide } from "swiper/react"
import type { Swiper as SwiperType } from "swiper"
import { Keyboard } from "swiper/modules"
import { cn } from "@/core/lib/utils"
import CustomButton from "../../components/CustomButton"

import "swiper/css"

type ImageType = {
  url: string
  alt?: string
  description?: string
}

type ProductGalleryProps = {
  images?: ImageType[]
  product?: {
    title?: string
    discount_percent?: number
    discount_title?: string
    thumbnail?: ImageType
  }
}

export default function ProductGallery({ images = [], product }: ProductGalleryProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null)
  const [modalSwiper, setModalSwiper] = useState<SwiperType | null>(null)

  const totalImages = images.length

  useEffect(() => {
    document.body.style.overflow = isGalleryOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isGalleryOpen])

  // فقط موقع باز شدن مودال، یک بار اسلاید رو تنظیم می‌کنیم
  useEffect(() => {
    if (isGalleryOpen && modalSwiper) {
      // سومین آرگومان false → بدون انیمیشن و بدون تریگر کردن eventهای اضافی
      modalSwiper.slideTo(activeIndex, 0, false)
    }
  }, [isGalleryOpen, modalSwiper]) // ← activeIndex رو حذف کردیم → حلقه بی‌نهایت از بین می‌ره

  if (totalImages === 0) return null

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const discountPercent = product?.discount_percent ?? 0
  const discountTitle = product?.discount_title ?? ""

  return (
    <div className="w-full">
      <div className="relative group">
        {/* Main Gallery - RTL */}
        <div className="relative w-full h-[50vh] overflow-hidden">
          <Swiper
            dir="rtl"
            modules={[Keyboard]}
            slidesPerView={1}   // موبایل پیشفرض ۱
            spaceBetween={0}
            loop={totalImages > 1}
            keyboard={{ enabled: true }}
            onSwiper={setMainSwiper}
            breakpoints={{
              1024: {
                slidesPerView: "auto",
                spaceBetween: 16,
              },
            }}
            onSlideChange={(s) => setActiveIndex(s.realIndex)}
            className="w-full h-full"
          >
            {images.map((img, index) => (
              <SwiperSlide key={index} className="w-full! lg:w-auto! h-full!">
                <div className="relative flex justify-center h-full w-auto">
                  <Image
                    src={img.url || "/placeholder.svg"}
                    alt={img.description || img.alt || product?.title || "تصویر محصول"}
                    width={1920}
                    height={1080}
                    className="h-full w-auto object-cover"
                    sizes="(max-width: 767px) 100vw, 55vw"
                    priority={index < 2}
                  />
                  {discountPercent > 0 && index === 0 && (
                    <div className="absolute top-4 left-4 bg-destructive text-primary-0 px-3 py-1.5 text-sm z-10">
                      {discountPercent}% {discountTitle}
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Desktop Controls */}
          {totalImages > 1 && (
            <div className="absolute flex lg:flex-col w-full justify-between lg:items-start lg:justify-center lg: max-lg:right-0 max-lg:left-0 top-0 bottom-0 z-20 items-center">
              <CustomButton
                size="icon"
                variant="black"
                onClick={() => mainSwiper?.slidePrev()}
                className="rounded-none size-12 flex"
              >
                <ChevronRight className="w-5 h-5" />
              </CustomButton>
              <CustomButton
                size="icon"
                variant="black"
                onClick={() => mainSwiper?.slideNext()}
                className="rounded-none size-12 flex"
              >
                <ChevronLeft className="w-5 h-5" />
              </CustomButton>
              {/* <CustomButton
                size="icon"
                variant="black"
                onClick={() => setIsGalleryOpen(true)}
                className="rounded-full"
              >
                <Plus className="w-5 h-5" />
              </CustomButton> */}
            </div>
          )}

          {/* Mobile Dots */}
          {/* {totalImages > 1 && (
            <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2.5 lg:hidden">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => mainSwiper?.slideTo(idx)}
                  className={cn(
                    "w-2.5 h-2.5 rounded-full transition-all duration-300",
                    activeIndex === idx ? "bg-primary scale-125 shadow-sm" : "bg-white/50 hover:bg-white/80"
                  )}
                />
              ))}
            </div>
          )} */}

          {/* Desktop Counter */}
          {totalImages > 1 && (
            <div className="absolute bottom-4 left-4 text-xs tracking-[0.25em] text-primary-900 z-20 bg-primary-200/70 backdrop-blur-sm px-3 py-1.5 rounded-xs hidden lg:block">
              {activeIndex + 1} / {totalImages}
            </div>
          )}
        </div>
      </div>

      {/* Modal - RTL */}
      {isGalleryOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 animate-in fade-in duration-300">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4">
            <span className="text-foreground font-medium text-lg">
              {activeIndex + 1} / {totalImages}
            </span>
            <div className="flex items-center gap-2">
              <CustomButton
                size="icon"
                variant="ghost"
                onClick={toggleFullscreen}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <Maximize2 className="w-5 h-5" />
              </CustomButton>
              <CustomButton
                size="icon"
                variant="ghost"
                onClick={() => setIsGalleryOpen(false)}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <X className="w-6 h-6" />
              </CustomButton>
            </div>
          </div>

          {/* Modal Navigation */}
          {totalImages > 1 && (
            <>
              <CustomButton
                size="icon"
                onClick={() => modalSwiper?.slideNext()}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 h-14 w-14 bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm rounded-full"
              >
                <ChevronRight className="w-7 h-7" />
              </CustomButton>
              <CustomButton
                size="icon"
                onClick={() => modalSwiper?.slidePrev()}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 h-14 w-14 bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm rounded-full"
              >
                <ChevronLeft className="w-7 h-7" />
              </CustomButton>
            </>
          )}

          <Swiper
            dir="rtl"
            modules={[Keyboard]}
            slidesPerView={1}
            loop={totalImages > 1}
            keyboard={{ enabled: true }}
            initialSlide={activeIndex} // کمک می‌کنه موقع باز شدن تقریباً درست باشه
            onSwiper={setModalSwiper}
            onSlideChange={(s) => setActiveIndex(s.realIndex)}
            className="w-full h-full"
          >
            {images.map((img, i) => (
              <SwiperSlide key={i} className="!flex items-center justify-center">
                <Image
                  src={img.url || "/placeholder.svg"}
                  alt={img.description || img.alt || "تصویر محصول"}
                  fill
                  className="object-contain px-4 lg:px-20"
                  priority={i === activeIndex}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Modal Thumbnails */}
          {totalImages > 1 && (
            <div className="absolute bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-black/60 to-transparent">
              <div className="flex gap-2 justify-center overflow-x-auto py-2 max-w-screen-lg mx-auto flex-row-reverse">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => modalSwiper?.slideTo(index)}
                    className={cn(
                      "relative shrink-0 w-16 h-16 lg:w-20 lg:h-20 overflow-hidden border-2 rounded-lg transition-all duration-300",
                      activeIndex === index
                        ? "border-white scale-110 shadow-xl"
                        : "border-transparent opacity-70 hover:opacity-100 hover:scale-105"
                    )}
                  >
                    <Image
                      src={img.url || "/placeholder.svg"}
                      alt={`Thumbnail ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="80px"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}